import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, FormGroup, FormLabel, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import MainLogo from "../../../images/main-logo.svg";
import AuthLeftSection from '../Auth/AuthLeftSection/AuthLeftSection.tsx';
import AuthBackground from '../Auth/AuthBackground/AuthBackground.tsx';
import LoginImage from "../../../images/signup-image.png";
import apiConfig from '../config/apiConfig.js';
import { legacyContactStep2Schema } from '../Auth/utils/validationSchema';
import { getRawPhoneDigits } from '../Auth/utils/phoneFormatter.js';
import { API_ENDPOINTS } from '../Auth/utils/constants.js';
import Header from '../Header/Header.tsx';
import Footer from '../Footer/Footer.tsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./Profile.scss";
import StepOneForm from './StepOneForm.tsx';
import CustomSelect from '../../common/CustomSelect/CustomSelect.tsx';
import { motion, AnimatePresence } from "framer-motion";

function LegacyContactSetup() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);

    // Check if user already completed legacy contact setup or doesn't have active subscription
    useEffect(() => {
      const checkUserStatus = async () => {
        try {
          // First check if account is active
          if (localStorage.getItem("accountStatus") !== "Active") {
            navigate("/subscription");
            return;
          }

          // Then check if legacy contact is already completed
          const authToken = localStorage.getItem('authToken');
          if (!authToken) {
            navigate("/");
            return;
          }

          const response = await axios.get(`${apiConfig.apiBaseURL}/profile`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          });

          // If legacyContact is true, user has already completed this step
          if (response.data && response.data.legacyContact === true) {
            toast.info('You have already completed the legacy contact setup!');
            navigate('/my-gifts');
          }
        } catch (error) {
          console.error('Error checking user status:', error);
          // If error, allow user to continue
        }
      };

      checkUserStatus();
    }, [navigate]);

    // Step 1 - Legacy Contact API Call
    const saveLegacyContacts = async (values: any) => {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
            toast.error('Authentication required. Please login again.');
            navigate('/');
            return false;
        }

        try {
           const contacts: Array<{
             name: string;
             email: string;
             phoneNumber: string;
             relationship: string;
             address1?: string;
             address2?: string;
             state?: string;
             city?: string;
             zip?: string;
           }> = [];

            if (values.primaryContactName) {
                contacts.push({
                    name: values.primaryContactName.trim(),
                    email: values.primaryEmail.trim(),
                    phoneNumber: getRawPhoneDigits(values.primaryPhone),
                    relationship: values.primaryRelationship.trim(),
                    address1: values.primaryAddress1?.trim() || '',
                    address2: values.primaryAddress2?.trim() || '',
                    state: values.primaryState || '',
                    city: values.primaryCity || '',
                    zip: values.primaryZip?.trim() || ''
                });
            }

            if (values.showSecondary && values.secondaryContactName) {
                contacts.push({
                    name: values.secondaryContactName.trim(),
                    email: values.secondaryEmail.trim(),
                    phoneNumber: getRawPhoneDigits(values.secondaryPhone),
                    relationship: values.secondaryRelationship.trim(),
                    address1: values.secondaryAddress1?.trim() || '',
                    address2: values.secondaryAddress2?.trim() || '',
                    state: values.secondaryState || '',
                    city: values.secondaryCity || '',
                    zip: values.secondaryZip?.trim() || ''
                });
            }

            const requestData = { contacts };

            const response = await axios.post(
                `${apiConfig.apiBaseURL}${API_ENDPOINTS.SAVE_LEGACY_CONTACT}`,
                requestData,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`
                    },
                }
            );

            if (response.data.status === "Success") {
                toast.success(response.data.message || 'Legacy contacts saved successfully!');
                return true;
            } else {
                toast.error(response.data.message || 'Failed to save legacy contacts. Please try again.');
                return false;
            }
        } catch (error) {
            console.error('Legacy contact save failed', error);
            toast.error(error.response?.data?.message || 'Failed to save legacy contacts. Please try again.');
            return false;
        }
    };

    // Step 2 - How Heard About Us API Call
    const saveHowHeardAboutUs = async (hearAboutValue:any) => {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
            toast.error('Authentication required. Please login again.');
            navigate('/');
            return false;
        }

        try {
            const response = await axios.post(
                `${apiConfig.apiBaseURL}${API_ENDPOINTS.SAVE_HOW_HEARD_ABOUT_US}`,
                { howHeardAboutUs: hearAboutValue },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`
                    },
                }
            );

            if (response.data.status === "Success") {
                // Clean up localStorage and navigate
                localStorage.removeItem("pendingUser");
                localStorage.removeItem("verifiedEmail");
                toast.success(response.data.message || 'Profile setup completed successfully!');
                navigate('/my-gifts');
                return true;
            } else {
                toast.error(response.data.message || 'Failed to complete profile setup. Please try again.');
                return false;
            }
        } catch (error) {
            console.error('How heard about us save failed', error);
            toast.error(error.response?.data?.message || 'Failed to complete profile setup. Please try again.');
            return false;
        }
    };

    const handleSkipStep2 = async () => {
        localStorage.removeItem("pendingUser");
        localStorage.removeItem("verifiedEmail");
        toast.success('Profile setup completed successfully!');
        navigate('/my-gifts');
    };

    const handleStepOneComplete = async (formData: any) => {
        const success = await saveLegacyContacts(formData);
        if (success) {
            setCurrentStep(2);
        }
    };

    // Step 2 Formik with centralized validation schema
    const step2Formik = useFormik({
        initialValues: {
            hearAbout: '',
        },
        validationSchema: legacyContactStep2Schema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await saveHowHeardAboutUs(values.hearAbout);
            } catch (error) {
                toast.error('An error occurred. Please try again.');
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.8,
                ease: "easeOut",
                staggerChildren: 0.2
            }
        }
    };

    const slideInLeft = {
        hidden: { opacity: 0, x: -60, scale: 0.95 },
        visible: {
            opacity: 1,
            x: 0,
            scale: 1,
            transition: {
                duration: 1,
                ease: [0.25, 0.46, 0.45, 0.94]
            }
        }
    };

    const slideInRight = {
        hidden: { opacity: 0, x: 60, scale: 0.95 },
        visible: {
            opacity: 1,
            x: 0,
            scale: 1,
            transition: {
                duration: 1,
                ease: [0.25, 0.46, 0.45, 0.94],
                delay: 0.3
            }
        }
    };

    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        }
    };

    const floatingAnimation = {
        animate: {
            y: [0, -8, 0],
            transition: {
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
            }
        }
    };

    return (
      <>
      <Header activeTab={undefined} toggle={undefined} />

      {/* Full-screen background animations */}
      <AuthBackground />

      <motion.section
        className="login-screen py-5"
        variants={containerVariants}
        initial="hidden"
         animate="visible"
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 30px', position: 'relative', zIndex: 1 }}
      >
         <div style={{ maxWidth: '1600px', width: '100%' }}>
        <div className="row" style={{ width: '100%', margin: '0' }}>
          {/* Left Panel - Enhanced Image and Content */}
          <AuthLeftSection
            image={LoginImage}
            imageAlt="Legacy Contact Setup"
            title="Giving Presence to Future Moments"
            description="We're more than a service—it's a movement to redefine how love endures."
            slideInLeft={slideInLeft}
            fadeInUp={fadeInUp}
            floatingAnimation={floatingAnimation}
          />

          {/* RIGHT FORM SIDE */}
          <div className="col-md-6" style={{ display: 'flex' }}>
            <motion.div
              className="login-screen-right"
              variants={slideInRight}
              initial="hidden"
              animate="visible"
              style={{
                height: 'calc(100vh - 20px)',
                overflowY: 'auto',
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div className="login-screen-right-in" style={{ justifyContent: 'flex-start', height: 'auto', minHeight: '100%' }}>
              <div className="middle-block">
                <motion.figure
                  className="main-logo"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    duration: 0.8,
                    ease: "easeOut",
                    delay: 0.8
                  }}
                  whileHover={{ scale: 1.05 }}
                >
                  <img src={MainLogo} alt="Main Logo" />
                </motion.figure>

                <AnimatePresence mode="wait">
                  {currentStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.5 }}
                    >
                      <StepOneForm onStepComplete={handleStepOneComplete} />
                    </motion.div>
                  )}

                  {currentStep === 2 && (
                    <motion.div
                      key="step2"
                      className="step-2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.5 }}
                    >
                      <motion.h3
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.6,
                          ease: "easeOut",
                          delay: 1
                        }}
                        style={{ marginBottom: '2rem', color: '#2c3e50' }}
                      >
                        How did you hear about <br />
                        Gifts From Heaven?
                      </motion.h3>
                      <Form
                      className='py-0'
                        onSubmit={(e) => {
                          e.preventDefault();
                          step2Formik.handleSubmit();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            step2Formik.handleSubmit();
                          }
                        }}
                      >
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.2, duration: 0.6 }}
                        >
                          <FormGroup className="form-group">
                            <FormLabel style={{ fontWeight: '600', color: '#2c3e50' }}>Select *</FormLabel>
                            <CustomSelect
                              name="hearAbout"
                              value={step2Formik.values.hearAbout}
                              onChange={(value) => step2Formik.setFieldValue('hearAbout', value)}
                              options={[
                                { value: 'friend', label: 'Friend' },
                                { value: 'family', label: 'Family' },
                                { value: 'socialMedia', label: 'Social Media' },
                                { value: 'advertisement', label: 'Advertisement' },
                                { value: 'other', label: 'Other' }
                              ]}
                              placeholder="-- Please Select --"
                              disabled={step2Formik.isSubmitting}
                              isInvalid={!!(step2Formik.touched.hearAbout && step2Formik.errors.hearAbout)}
                            />
                            <AnimatePresence>
                              {step2Formik.touched.hearAbout &&
                                step2Formik.errors.hearAbout && (
                                  <motion.small
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    style={{
                                      color: "#dc3545",
                                      display: "block",
                                      marginTop: "8px",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {step2Formik.errors.hearAbout}
                                  </motion.small>
                                )}
                            </AnimatePresence>
                          </FormGroup>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.4, duration: 0.6 }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="mt-4"
                        >
                          <Button
                            className="main-bttn"
                            type="submit"
                            disabled={step2Formik.isSubmitting}
                            style={{
                              width: '100%',
                              padding: '14px 20px',
                              background: step2Formik.isSubmitting
                                ? 'linear-gradient(135deg, #bbb, #999)'
                                : '#D7A360',
                              border: 'none',
                              borderRadius: '12px',
                              fontSize: '16px',
                              fontWeight: '600',
                              boxShadow: '0 8px 20px rgba(215, 163, 96, 0.3)',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <AnimatePresence mode="wait">
                              {step2Formik.isSubmitting ? (
                                <motion.span
                                  key="loading"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                >
                                  <motion.span
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                    style={{ display: 'inline-block', marginRight: '8px' }}
                                  >
                                    ⟳
                                  </motion.span>
                                  Submitting...
                                </motion.span>
                              ) : (
                                <motion.span
                                  key="submit"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                >
                                  Submit
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </Button>
                        </motion.div>

                        <motion.div
                          className="skip"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 1.6, duration: 0.6 }}
                          style={{ textAlign: 'center', marginTop: '1.5rem' }}
                        >
                          <motion.div whileHover={{ scale: 1.05 }}>
                            <Button
                              variant="link"
                              type="button"
                              onClick={handleSkipStep2}
                              className="p-0 text-decoration-none skip-link"
                              disabled={step2Formik.isSubmitting}
                              style={{
                                color: '#3957A0',
                                fontSize: '18px',
                                fontWeight: '600'
                              }}
                            >
                              Skip
                            </Button>
                          </motion.div>
                        </motion.div>
                      </Form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            </motion.div>
          </div>
        </div>
        </div>
      </motion.section>
      <Footer />
      </>
    );
}

export default LegacyContactSetup;
